/**
 * format.ts — даты, числа и суммы на языке кабинета (WEB-1874, этап 2).
 *
 * ЕДИНСТВЕННОЕ место кабинета, где разрешено писать строку локали
 * (`'ru-RU'` / `'en-US'`). Сторож
 * `__tests__/cabinet-strings-locale.web1874.test.ts` красит такие литералы
 * везде, кроме этого файла и постоянных исключений: словарь их не лечит —
 * «29 августа 2026» против «August 29, 2026» рисует `Intl`, а не наш текст,
 * и на английском кабинете 72 зашитых `'ru-RU'` печатали бы русский формат.
 *
 * Локаль у всех функций — последний, необязательный параметр: по умолчанию
 * текущая локаль кабинета (`getUiLocale()`). Явная локаль нужна там, где
 * формат зафиксирован намеренно (например пин `en` в `utils/formatCents.ts`,
 * чтобы доллар печатался как «$2.44», а не «US$2.44»).
 */
import { getUiLocale, type UiLocale } from './locale';

const INTL_TAG: Record<UiLocale, string> = { en: 'en-US', ru: 'ru-RU' };

/** Тег BCP-47 для `Intl` по языку кабинета. */
export function intlLocale(locale: UiLocale = getUiLocale()): string {
  return INTL_TAG[locale];
}

/** Число: разделители разрядов и дробной части по языку. */
export function formatNumber(
  value: number,
  options?: Intl.NumberFormatOptions,
  locale: UiLocale = getUiLocale(),
): string {
  return new Intl.NumberFormat(intlLocale(locale), options).format(value);
}

/**
 * Готовый форматтер даты-времени — для мест, которые собирают строку из
 * частей (`formatToParts`), потому что им нужен свой порядок и разделители.
 */
export function dateTimeFormat(
  options?: Intl.DateTimeFormatOptions,
  locale: UiLocale = getUiLocale(),
): Intl.DateTimeFormat {
  return new Intl.DateTimeFormat(intlLocale(locale), options);
}

/** Приводит вход к дате; `null` — если момента нет или он неразбираем. */
export function toDate(value: Date | string | number | null | undefined): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Дата. Неразбираемое значение даёт пустую строку, а не «Invalid Date»:
 * вызывающий сам решает, что показать вместо неё («—», прочерк, ничего).
 */
export function formatDate(
  value: Date | string | number | null | undefined,
  options: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit', year: 'numeric' },
  locale: UiLocale = getUiLocale(),
): string {
  const date = toDate(value);
  return date === null ? '' : dateTimeFormat(options, locale).format(date);
}

/**
 * Дата со временем. Та же дисциплина пустой строки, что у formatDate.
 *
 * Часовой цикл просим ПРЯМО (`hourCycle: 'h23'`), а не флагом `hour12: false`
 * (ревизия пачки 15, R1-15). Флаг говорит только «не 12-часовой» и оставляет
 * выбор между `h23` (полночь — «00:00») и `h24` (полночь — «24:00») локали и
 * версии ICU: часа «24» в сутках не существует, и на сборке, где ICU выбирает
 * `h24`, кабинет печатал бы несуществующее время. `h23` называет ровно то, что
 * мы хотим, и не зависит от версии.
 */
export function formatDateTime(
  value: Date | string | number | null | undefined,
  options: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  },
  locale: UiLocale = getUiLocale(),
): string {
  return formatDate(value, options, locale);
}

/**
 * ТОЛЬКО ВРЕМЯ — «14:05» (WEB-2090, остаток WEB-1368 п.2.17).
 *
 * Зачем отдельная дверь. Строке свежести «Обновлено в 14:05» дата не нужна:
 * пульс перечитывают в течение одной смены, и дата в ней — шум. Набором
 * опций по месту это заводить нельзя (WEB-1844): `new Date(ts)
 * .toLocaleTimeString(undefined, …)` печатает время на языке БРАУЗЕРА, а не
 * кабинета, и мимо `intlLocale` — ровно то, что ловит сторож локализации
 * `__tests__/cabinet-strings-locale.web1874.test.ts`.
 *
 * Часовой цикл — `h23` по той же причине, что у `formatDateTime`.
 */
export function formatTime(
  value: Date | string | number | null | undefined,
  locale: UiLocale = getUiLocale(),
): string {
  return formatDate(value, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }, locale);
}

/**
 * Дата со временем БЕЗ ГОДА — «12.09, 02:14» (WEB-1198, блок 9).
 *
 * Зачем отдельная дверь. В журнале доставок и в таблице ключей год ничего не
 * добавляет — там всё за последние сутки-месяц (доставки храним 30 суток), а
 * четыре лишних символа в каждой ячейке раздвигали колонку и выталкивали
 * таблицу за край карточки. Общий `formatDateTime` при этом НЕ трогаем: в
 * денежных сверках и журнале аудита год — рабочая величина.
 *
 * Год не выкидывается «строкой по запятой», а просто не запрашивается у
 * форматтера: порядок частей в разных локалях свой, и резать вывод было бы
 * гаданием.
 */
export function formatDateTimeNoYear(
  value: Date | string | number | null | undefined,
  locale: UiLocale = getUiLocale(),
): string {
  return formatDate(
    value,
    { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' },
    locale,
  );
}

/**
 * Дата со временем И СЕКУНДАМИ — для ЖУРНАЛОВ (ревизия пачки 16, R3-2).
 *
 * Зачем отдельная дверь. В журнале аудита, истории входов, ленте ошибок,
 * денежных сверках и списке худших генераций секунды — рабочая величина: по
 * ним восстанавливают порядок событий внутри одной минуты (кто раньше нажал,
 * что за чем упало). До перевода панели они печатались сами собой —
 * `new Date(v).toLocaleString('ru-RU')` показывает секунды по умолчанию, — а
 * общий `formatDateTime` их не просит, и журналы их молча потеряли.
 *
 * Заводить это набором опций по месту нельзя: пять экранов = пять копий
 * одного правила, и они разойдутся на первой же правке (WEB-1844). Русский
 * вид совпадает с прежним `toLocaleString('ru-RU')` БАЙТ-В-БАЙТ — сторож
 * `__tests__/format.test.ts` держит это сравнением с самим `toLocaleString`.
 *
 * ⚠ Часовой пояс не задаётся намеренно: журнал показывает МЕСТНОЕ время
 * дежурного, ровно как показывал раньше.
 */
export function formatDateTimeSeconds(
  value: Date | string | number | null | undefined,
  locale: UiLocale = getUiLocale(),
): string {
  return formatDate(
    value,
    {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    },
    locale,
  );
}

/**
 * Сумма в единицах валюты (не в наименьших!) с её знаком.
 *
 * Число знаков после запятой берёт сам `Intl` из кода валюты: у иены и воны
 * дробной части нет, и «¥1,000» печатается без копеек. Пересчёт наименьших
 * единиц в отображаемые — не здесь, а в `utils/zeroDecimalCurrencies.ts`
 * (одно правило, зеркало бэкендового).
 *
 * WEB-4125: код валюты, который `Intl` не принимает (`RangeError: Invalid
 * currency code`, например «USDX»), ронял весь кабинет из окна «Журнал
 * списаний». Страховка здесь, а не у каждого вызывающего: запасной вид —
 * число по локали и код заглавными через пробел («12,50 USDX»): сумма
 * печатается как доллар (2 знака, как `currencyFractionDigits` считает любую
 * валюту вне справочника), срезается только знак доллара — буквы компактной
 * записи («50K», «50 тыс.») остаются. Коротко нарочно: `format.ts`
 * входит в первый экран публичной страницы (храповик веса WEB-1988).
 * Прочие ошибки не глотаются.
 */
export function formatMoney(
  amount: number,
  currency: string,
  options?: Intl.NumberFormatOptions,
  locale: UiLocale = getUiLocale(),
): string {
  const code = currency.toUpperCase();
  try {
    return new Intl.NumberFormat(intlLocale(locale), {
      style: 'currency',
      currency: code,
      ...options,
    }).format(amount);
  } catch (err) {
    if (!(err instanceof RangeError) || code === 'USD') throw err;
    return `${formatMoney(amount, 'USD', options, locale).replace(/US\$|\$/, '').trim()} ${code}`;
  }
}

/**
 * `Intl.Segmenter` есть в Node 16+ и во всех живых браузерах, но в
 * подключённой библиотеке типов (ES2020) его описания нет — описываем ровно то,
 * чем пользуемся.
 */
type GraphemeSegmenter = new (
  locale: string | undefined,
  options: { granularity: 'grapheme' },
) => { segment(text: string): Iterable<unknown> };
const Segmenter = (Intl as unknown as { Segmenter?: GraphemeSegmenter }).Segmenter;

/**
 * Число видимых символов (графем): флаг, семья, эмодзи с оттенком кожи —
 * один символ из нескольких точек кода. Деление на графемы от языка не
 * зависит, но `Intl` в кабинете живёт только здесь (сторож WEB-1874 (5)).
 * Нужен проверке «ровно один эмодзи» у значка варианта (optionGrid.ts).
 */
export function graphemeCount(text: string, locale: UiLocale = getUiLocale()): number {
  // Старый браузер без Segmenter: счёт по точкам кода — составной эмодзи
  // выйдет «не одним», значок просто не нарисуется, страница не падает.
  if (!Segmenter) return Array.from(text).length;
  const segmenter = new Segmenter(intlLocale(locale), { granularity: 'grapheme' });
  return [...segmenter.segment(text)].length;
}
